import { useState, type FormEvent } from "react";
import { Link, useLocation } from "react-router-dom";
import { apiJson } from "../lib/queryClient";

export default function SetupPassword() {
  const location = useLocation();
  const token = new URLSearchParams(location.hash.replace(/^#/, "")).get("token") ?? "";
  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [complete, setComplete] = useState(false);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    if (password !== confirmation) {
      setError("Die Passwörter stimmen nicht überein.");
      return;
    }

    setSubmitting(true);
    try {
      await apiJson("POST", "/api/auth/password-setup/complete", {
        token,
        newPassword: password,
      });
      // Remove the bearer token from the address bar immediately after use.
      window.history.replaceState(null, "", location.pathname);
      setComplete(true);
      setPassword("");
      setConfirmation("");
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main className="min-h-screen flex items-center justify-center p-4 bg-[var(--color-app-bg)]">
      <section className="w-full max-w-md rounded-xl border border-[var(--color-app-border)] bg-white p-6 shadow-sm">
        <h1 className="text-2xl font-semibold text-[var(--color-app-text)]">
          Passwort festlegen
        </h1>

        {complete ? (
          <div className="mt-4 space-y-4">
            <p className="text-sm text-[var(--color-app-text-secondary)]">
              Dein Passwort wurde festgelegt. Du kannst dich jetzt mit deiner E-Mail-Adresse und
              dem neuen Passwort anmelden.
            </p>
            <Link
              to="/login"
              className="inline-flex px-4 py-2 rounded-md bg-brand-600 hover:bg-brand-700 text-white text-sm"
            >
              Zur Anmeldung
            </Link>
          </div>
        ) : !token ? (
          <div className="mt-4 space-y-4">
            <p role="alert" className="text-sm text-[var(--color-nogo)]">
              Dieser Setup-Link ist unvollständig. Bitte frage den Administrator nach einem neuen
              Link.
            </p>
            <Link to="/login" className="text-sm text-[var(--color-brand-600)] underline">
              Zur Anmeldung
            </Link>
          </div>
        ) : (
          <>
            <p className="mt-2 text-sm text-[var(--color-app-text-secondary)]">
              Wähle ein Passwort mit mindestens 8 Zeichen. Der Einrichtungslink kann nur einmal
              verwendet werden.
            </p>
            <form onSubmit={submit} className="mt-5 space-y-4">
              <label className="block text-sm font-medium text-[var(--color-app-text)]">
                Neues Passwort
                <input
                  type="password"
                  required
                  minLength={8}
                  autoComplete="new-password"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  className="mt-1 w-full px-3 py-2 border border-[var(--color-app-border)] rounded-md text-sm"
                />
              </label>
              <label className="block text-sm font-medium text-[var(--color-app-text)]">
                Passwort wiederholen
                <input
                  type="password"
                  required
                  minLength={8}
                  autoComplete="new-password"
                  value={confirmation}
                  onChange={(event) => setConfirmation(event.target.value)}
                  className="mt-1 w-full px-3 py-2 border border-[var(--color-app-border)] rounded-md text-sm"
                />
              </label>
              {error && (
                <p role="alert" className="text-sm text-[var(--color-nogo)]">
                  {error}
                </p>
              )}
              <button
                type="submit"
                disabled={submitting || !token}
                className="w-full px-4 py-2 rounded-md bg-brand-600 hover:bg-brand-700 text-white text-sm disabled:opacity-50"
              >
                {submitting ? "Wird gespeichert …" : "Passwort festlegen"}
              </button>
            </form>
          </>
        )}
      </section>
    </main>
  );
}